import { DeployEnv, Site, SLD } from '@patsnap-utils/biz';

/**
 * LLM 模型选择工具
 *
 * 模型由 home-plugin-sdk 的模型切换组件写入 localStorage，
 * 各业务项目在请求底层（PsHttpService.options.headers）读取并注入 header。
 *
 * 存储约定必须与 home-plugin-sdk/src/utils/llm-model.ts 保持一致。
 */

export const DEFAULT_LLM_MODEL = 'Auto';

export const LLM_MODEL_STORAGE_PREFIX = 'rd-home-sdk:llm-model';

/**
 * 各业务项目对应的 functionId（写死，与 home-plugin-sdk 的 FunctionId 枚举一致）。
 * 用作读取 localStorage 缓存的 key 后缀。
 */
export const LLM_FUNCTION_ID = {
    TRIZ: 'rd_triz',
    QUICK_RESEARCH: 'rd_research',
    TECH_MIND: 'rd_air_find_solution_agent',
    FMEA: 'rd_fmea',
} as const;

export function isLlmModelEnabled(): boolean {
    try {
        const env = new Site({ sld: SLD.EUREKA }).env;
        return env !== DeployEnv.US && env !== DeployEnv.EU;
    } catch {
        return true;
    }
}

export function getLlmModelStorageKey(functionId: string): string {
    return `${LLM_MODEL_STORAGE_PREFIX}:${functionId}`;
}

/**
 * 归一化模型值：非法值归一为 Auto。
 * @param validValues 可选的合法模型值列表（由 SDK 的 LLM_MODEL_OPTIONS 派生传入）。
 *        不传时仅校验非空，信任写入侧已归一过的缓存值。
 */
export function normalizeLlmModel(model?: string | null, validValues?: string[]): string {
    if (!model) return DEFAULT_LLM_MODEL;
    if (validValues && !validValues.includes(model)) return DEFAULT_LLM_MODEL;
    return model;
}

export function getStoredLlmModel(functionId?: string, validValues?: string[]): string {
    if (!functionId || typeof localStorage === 'undefined') return DEFAULT_LLM_MODEL;
    try {
        return normalizeLlmModel(localStorage.getItem(getLlmModelStorageKey(functionId)), validValues);
    } catch {
        return DEFAULT_LLM_MODEL;
    }
}

export function setStoredLlmModel(functionId: string, model: string, validValues?: string[]): void {
    if (!functionId || typeof localStorage === 'undefined') return;
    try {
        localStorage.setItem(getLlmModelStorageKey(functionId), normalizeLlmModel(model, validValues));
    } catch {
        // ignore storage write failure
    }
}

/**
 * 生成请求 header，Auto 或未启用时返回空对象（由后端使用默认模型）。
 * @param functionId 业务项目写死的 functionId，如 LLM_FUNCTION_ID.TRIZ
 */
export function getLlmModelHeader(functionId?: string): Record<string, string> {
    if (!isLlmModelEnabled()) return {};

    const model = getStoredLlmModel(functionId);
    return model === DEFAULT_LLM_MODEL ? {} : { 'X-Select-Model': model };
}

/**
 * 获取传给请求 body 的 model 参数（值与公司模型网关保持一致）。
 * Auto 或未启用时不传，由后端使用默认模型。
 */
export function getLlmModelParam(functionId?: string): { model?: string } {
    if (!isLlmModelEnabled()) return {};

    const model = getStoredLlmModel(functionId);
    return model === DEFAULT_LLM_MODEL ? {} : { model };
}
